import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { StripeProvider } from './contexts/StripeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import NotificationProvider from './components/NotificationSystem';
import Header from './components/Header';
import SessionsSidebar from './components/SessionsSidebar';
import ClientApp from './components/ClientApp';
import InstructorLogin from './components/InstructorLogin';
import InstructorApp from './components/InstructorApp';
import MobileOptimizedDashboard from './components/MobileOptimizedDashboard';
import InstructorProfile from './components/InstructorProfile';
import EditProfile from './components/EditProfile';
import WorkshopDetail from './components/WorkshopDetail';
import BookingSuccess from './components/BookingSuccess';
import CreateWorkshop from './components/CreateWorkshop';
import EditWorkshop from './components/EditWorkshop';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionGuard from './components/SubscriptionGuard';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionDebug from './components/SubscriptionDebug';
import InstructorCalendarCallback from './components/InstructorCalendarCallback';
import EmbeddedWorkshop from './components/EmbeddedWorkshop';

import './index.css';

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const InstructorLayout = ({ children }) => (
    <div className="flex h-screen bg-klorah-dark text-klorah-text overflow-hidden">
      {/* Desktop Sidebar */}
      <SessionsSidebar 
        isOpen={isMobileMenuOpen} 
        setIsOpen={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div 
        className="flex-1 flex flex-col"
        onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
      >
        {/* Mobile Header */}
        <Header 
          isInstructorRoute={true}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        
        {/* Content Area */}
        <main className="flex-1 overflow-auto bg-klorah-dark">
          {children}
        </main>
      </div>
    </div>
  );

  const PublicLayout = ({ children }) => (
    <div className="min-h-screen bg-klorah-dark">
      <Header isInstructorRoute={false} />
      <main className="flex-1 relative">
        {children}
      </main>
    </div>
  );

  return (
    <AuthProvider>
      <StripeProvider>
        <SubscriptionProvider>
          <NotificationProvider>
            <Router>
              <div className="min-h-screen bg-klorah-dark text-klorah-text">
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={
                    <PublicLayout>
                      <ClientApp />
                    </PublicLayout>
                  } />
                  <Route path="/workshops/:id" element={
                    <PublicLayout>
                      <WorkshopDetail />
                    </PublicLayout>
                  } />
                  <Route path="/embed/workshop/:id" element={<EmbeddedWorkshop />} />
                  <Route path="/login" element={
                    <PublicLayout>
                      <InstructorLogin />
                    </PublicLayout>
                  } />
                  <Route path="/booking-success" element={
                    <PublicLayout>
                      <BookingSuccess />
                    </PublicLayout>
                  } />
                  <Route path="/calendar-callback" element={<InstructorCalendarCallback />} />
                  <Route path="/subscription-success" element={
                    <PublicLayout>
                      <SubscriptionSuccess />
                    </PublicLayout>
                  } />
                  
                  {/* Protected instructor routes with hard paywall */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <InstructorApp />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/mobile-dashboard" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <MobileOptimizedDashboard />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <InstructorProfile />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/edit-profile" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <EditProfile />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/create-workshop" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <CreateWorkshop />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/edit-workshop/:id" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <EditWorkshop />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/instructor-calendar-callback" element={<InstructorCalendarCallback />} />
                  <Route path="/subscription-management" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InstructorLayout>
                          <SubscriptionManagement />
                        </InstructorLayout>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />
                </Routes>
              </div>
              <SubscriptionDebug />
            </Router>
          </NotificationProvider>
        </SubscriptionProvider>
      </StripeProvider>
    </AuthProvider>
  );
}

export default App; 